import {
  reactQueryDelete,
  reactQueryGet,
  reactQueryPost,
  reactQueryPut,
} from "@circle-react/helpers/backendRequestHelpers";
import { internalApi } from "@circle-react/helpers/urlHelpers";
import type { SegmentPage } from "../components/SettingsApp/Segments/hooks/useSegmentsInfiniteQuery";
import type { CommunitySegmentRule } from "../components/SettingsApp/Workflows/generated/WorkflowsApi.types";

interface ListSegmentsParams {
  params: {
    page?: number;
    per_page?: number;
    ids?: number[];
  };
}
interface ListConsumersParams {
  id: number;
  params: {
    page?: number;
    per_page?: number;
    consumer_type?: "Workflow" | "Broadcast";
  };
}

const list = ({ params = {} }: ListSegmentsParams) =>
  reactQueryGet(internalApi.segments.index({ params }));

const createSegment = ({
  title,
  rules,
  visible,
  community_segment_consumer_attributes,
}: {
  title?: string;
  rules?: CommunitySegmentRule;
  visible?: boolean;
  community_segment_consumer_attributes?: {
    consumer_id: string | number;
    consumer_type: string;
  };
}) =>
  reactQueryPost<SegmentPage>(internalApi.segments.index(), {
    title,
    rules,
    visible,
    community_segment_consumer_attributes,
  });

const deleteSegment = ({ id }: { id: number }) =>
  reactQueryDelete(internalApi.segments.detail({ id }));

const showSegment = ({ id }: { id: number }) =>
  reactQueryGet(internalApi.segments.detail({ id }));

interface UpdateSegmentPayload {
  rules?: CommunitySegmentRule;
  title?: string;
}

interface UpdateSegmentParams extends UpdateSegmentPayload {
  id: number;
}

const updateSegment = ({ id, title, rules }: UpdateSegmentParams) =>
  reactQueryPut<SegmentPage, UpdateSegmentPayload>(
    internalApi.segments.detail({ id }),
    { title, rules },
  );

const duplicateSegment = ({ id, title }: { id: number; title: string }) =>
  reactQueryPost(internalApi.segments.duplicate({ id }), { title });

const usageStatus = ({ id }: { id: number }) =>
  reactQueryGet(internalApi.segments.usageStatus({ id }));

const consumers = ({ id, params = {} }: ListConsumersParams) =>
  reactQueryGet(internalApi.segments.consumers({ id, params }));

export const segmentsApi = {
  list,
  delete: deleteSegment,
  create: createSegment,
  show: showSegment,
  update: updateSegment,
  duplicate: duplicateSegment,
  usageStatus,
  consumers,
} as const;
